import React from 'react';
import { Modal, Color, Alert, Title, Text, Flex } from '@bb/elements-web';
import styled from 'styled-components';
import { useUpdatePaymentCardModalContext } from './UpdatePaymentCardModalContext';
import ManagePaymentMethodCard from './ManagePaymentMethodCard';
import { useSubscriptionsPageContext } from '../../pages/SubscriptionsPaymentsPage/SubscriptionsPageContext';

interface PayNowModalProps {
  open: boolean;
  onComplete: () => void;
  onCancel: () => void;
  completionDisabled: boolean;
  completionLoading: boolean;
  showCardErrorWarning: boolean;
}

/**
 * The modal that appears when a user clicks "Pay Now" on the overdue balance alert.
 */
export const PayNowModal: React.FC<PayNowModalProps> = ({
  open,
  onComplete,
  onCancel,
  completionDisabled,
  completionLoading,
  showCardErrorWarning,
}) => {
  const {
    overdueBalance,
    suspensionDate,
    isSuspended,
    billingProfile,
    isFetchingProfile,
    canManageBilling,
  } = useSubscriptionsPageContext();

  const { setModalVisible } = useUpdatePaymentCardModalContext();

  const formattedSuspensionDate = suspensionDate
    ? new Date(suspensionDate).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      })
    : '-';

  return (
    <Modal
      dataCy="overdue-payment-modal"
      open={open}
      title={
        <Title dataCy="overdue-payment-modal-title" level={4} margin={0}>
          Outstanding Balance
        </Title>
      }
      okText={'Pay Now'}
      width={515}
      okButtonProps={{
        style: { marginLeft: '0px' },
        autoFocus: true,
        disabled: !canManageBilling.response || completionDisabled,
        loading: completionLoading,
        block: true,
      }}
      onOk={onComplete}
      onCancel={onCancel}
      padding={24}
    >
      <Flex vertical gap={20}>
        <Alert
          type="error"
          showIcon
          style={{ padding: '16px' }}
          message={
            isSuspended ? (
              <Text size="large">
                Your paid service(s) have been suspended because of an
                outstanding balance. Please complete the payment to restore
                access.
              </Text>
            ) : (
              <Text size="large">
                You have an outstanding balance. Please complete the payment
                before {formattedSuspensionDate} so you don&apos;t lose access
                to your paid services.
              </Text>
            )
          }
        />
        <div>
          <Title level={4} dataCy="summary-header">
            Summary
          </Title>
          <StyledBalanceSection>
            <Text size="large">
              Outstanding balance{' '}
              <span style={{ color: Color.SECONDARY_TEXT }}>
                (+applicable taxes)
              </span>
            </Text>
            <Text size="large">{overdueBalance}</Text>
          </StyledBalanceSection>
        </div>

        <div>
          <Title level={4} dataCy="card-details-header">
            Card Details
          </Title>
          <ManagePaymentMethodCard
            onUpdateCardClick={() => setModalVisible(true)}
            showCardErrorWarning={showCardErrorWarning}
            billingProfile={billingProfile}
            isFetchingProfile={isFetchingProfile}
            canManageBilling={canManageBilling}
          />
        </div>
      </Flex>
    </Modal>
  );
};

const StyledBalanceSection = styled.div`
  background-color: ${Color.LIGHTER_GREY};
  padding: 16px;
  width: 100%;
  border-radius: 4px;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;
